/**
 * SHOWICT Quantum — wiring del indicador.
 *
 * REDISEÑO de presentación del indicador legacy `showi_ict` al formato
 * Quantum: la detección la sigue haciendo el motor original (intocable,
 * byte-idéntico en engine.js) y este módulo solo añade el filtro opcional de
 * activos favoritos, adapter de solo lectura + derivación de niveles
 * (adapter.js), estadísticas de dashboard con budget dual (stats.js) y
 * pintura Quantum (renderer.js), todo vía createChartDrawingApi.
 *
 * El legacy `showi_ict` sigue en el catálogo y coexistiendo en esta entrega
 * (migración v12 de useIndicatorState: quien lo tenía activo recibe este
 * rediseño encendido; la retirada del menú legacy es una fase posterior).
 */

import BadgeComponent from './Badge.jsx'
import PanelComponent from './Panel.jsx'
import {
  SAFE_DEFAULTS,
  resolveConfig,
  symbolMatches,
} from './constants.js'
import { runAdapter } from './adapter.js'
import { computeStats } from './stats.js'
import { renderShowict } from './renderer.js'
import { getChartContext } from './context.js'
import { toUtcSeconds } from '@/utils/utcTime'

export const defaultConfig = SAFE_DEFAULTS

export { resolveConfig }

function gatedResult() {
  return {
    signals: [],
    fakes: [],
    pivotLabels: [],
    activeLevels: { activeH: null, activeL: null, bullBias: true },
    activeSignal: null,
    lastSignal: null,
    stats: null,
    gated: true,
  }
}

/**
 * Sanea velas de entrada: OHLC numéricos y time presente. Devuelve null si
 * la entrada no es un array (los callers devuelven entonces salida gated).
 *
 * NORMALIZACIÓN DE TIEMPOS EN LA FRONTERA (v1.3.2): los times se convierten a
 * segundos UTC con el conversor compartido @/utils/utcTime ANTES de pasar las
 * velas al engine — en TFs diarios+ llegan como strings 'YYYY-MM-DD'
 * (business days) y todo matching estricto fallaría en silencio. El motor no
 * hace aritmética con time (solo lo traslada a su salida), así que la
 * conversión es segura y TODA la salida (signals, tpHits, closeTime,
 * pivotLabels) ya sale en segundos — el filtro del renderer compara entonces
 * segundos con segundos y la ventana de 90 días de stats.js opera sobre
 * segundos reales.
 */
function sanitizeCandles(candles) {
  if (!Array.isArray(candles)) return null
  const out = []
  for (const c of candles) {
    if (c == null || typeof c !== 'object') continue
    const open = Number(c.open)
    const high = Number(c.high)
    const low = Number(c.low)
    const close = Number(c.close)
    if (!Number.isFinite(open) || !Number.isFinite(high) || !Number.isFinite(low) || !Number.isFinite(close)) continue
    const time = toUtcSeconds(c.time)
    if (time == null) continue
    out.push({ time, open, high, low, close, volume: Number.isFinite(Number(c.volume)) ? Number(c.volume) : 0 })
  }
  return out
}

export function detect(candles, rawConfig) {
  try {
    const cfg = resolveConfig(rawConfig)

    // Apagado suave (patrón bollinger): el panel desactiva sin quitar el
    // indicador del registry.
    if (cfg.enabled === false) return gatedResult()

    // Gating por activo (contexto inyectado por la página). Lista vacía =
    // todos los símbolos (symbolMatches ya lo resuelve). Sin gating por
    // timeframe: el indicador corre siempre en el TF del chart.
    const ctx = getChartContext() || {}
    if (!symbolMatches(cfg.assets, ctx.symbol)) return gatedResult()

    const clean = sanitizeCandles(candles)
    if (!clean) return gatedResult()
    // Mínimo de barras que exige el motor (engine.js línea 51):
    // n < pivotLen * 2 + atrLen + 5 → null.
    const minBars = cfg.pivotLen * 2 + cfg.atrLen + 5
    if (clean.length < minBars) return gatedResult()

    const out = runAdapter(clean, cfg, ctx)
    // showLastSignals viaja en el result porque el Badge no recibe config (ni
    // DraggableRegistryBadge ni ChartPage se la pasan); es la única vía de
    // que el toggle del panel llegue a la tabla de últimas 10 señales.
    return { ...out, stats: computeStats(out.signals, cfg), showLastSignals: cfg.showLastSignals }
  } catch {
    return gatedResult()
  }
}

export function render(chart, candleSeries, result, config, api) {
  renderShowict(chart, candleSeries, result, config, api)
}

export default {
  id: 'showict_quantum',
  name: 'SHOWICT Quantum',
  version: '1.3.0',
  defaultConfig,
  detect,
  render,
  BadgeComponent,
  PanelComponent,
  // Anti-flicker (regla OB, mismo patrón que el resto de indicadores Quantum):
  // las primitivas de SHOWICT son series nativas de LWC (líneas, custom series
  // de relleno, markers) + labels HTML con su propio efecto de reposicionado —
  // TODAS panean solas. Reconstruirlas en cada frame de pan las hacía
  // aparecer/desaparecer y cambiar de forma (bug 2026-08-06): en pan puro el
  // registry NO las reconstruye; la geometría queda anclada a datos + motor.
  renderOnPan: false,
}
